/* HILO — phone number validation on lead forms.

   Problem: people were typing 4 or 5 digits into the Talk to Stylist form.
   The lead saved fine, but the telecaller had nothing to ring, so it was dead
   on arrival.

   Why the rule follows the country code
   -------------------------------------
   National numbers are NOT the same length everywhere:

       India, US, UK   10 digits
       China           11
       Australia        9
       UAE, Saudi       9
       Singapore        8

   The form collects the country code separately, so a flat "at least 10"
   would silently reject a genuine customer in Dubai or Singapore — exactly
   the NRI markets that buy wedding wear. So India gets the strict rule, and
   everywhere else gets a sane range.

   Why this is a separate file rather than an edit inside the form sections:
   the same rule has to cover the product-page popup AND the booking form on
   the Talk to Stylist page, and both handle their own submit. Guarding from
   outside means neither form's logic is touched, and deleting this file
   removes the rule cleanly.
*/
(function () {
  if (window.__hiloPhoneValidate) return;
  window.__hiloPhoneValidate = true;

  var IN_LEN      = 10;    /* Indian mobile, always 10 */
  var INTL_MIN    = 7;     /* shortest national number in real use */
  var INTL_MAX    = 12;
  var HARD_MAX    = 15;    /* E.164 ceiling, used for the input cap */

  var MSG_IN_LEN   = 'Please enter your full 10-digit mobile number.';
  var MSG_IN_START = 'An Indian mobile number starts with 6, 7, 8 or 9.';
  var MSG_INTL     = 'Please enter a valid mobile number for the country code selected.';
  var MSG_FAKE     = 'Please enter a real mobile number so our stylist can reach you.';

  function digits(v) { return String(v || '').replace(/[^0-9]/g, ''); }

  function isPhoneField(el) {
    if (!el || el.tagName !== 'INPUT' || el.type === 'hidden') return false;
    var hay = ((el.name || '') + ' ' + (el.id || '') + ' ' +
               (el.getAttribute('placeholder') || '') + ' ' +
               (el.className || '')).toLowerCase();

    /* --- NEVER treat these as phone fields, whatever else they are called ---

       This test used to include the bare word "contact", which looked
       reasonable (contact_number, contact-no) but quietly broke Shopify's
       own contact form. Shopify names those fields contact[name] and
       contact[email] — both contain "contact", so both were classified as
       phone fields, and the input handler below stripped every non-digit
       from them. Typing a name or an email address wiped itself character
       by character, and submitting showed "Please enter your full 10-digit
       mobile number" underneath the Name box.

       Two guards now: an explicit deny-list of field purposes, and a
       narrower phone test that only accepts "contact" when it is followed
       by a number-ish word. */
    if (/name|email|e-mail|mail|message|body|subject|comment|address|city|state|zip|pincode|postal|company|website|url/.test(hay)) return false;

    /* Never coerce a typed email or url input, regardless of its name. */
    if (el.type === 'email' || el.type === 'url') return false;

    /* "country code" fields often also say phone — exclude them explicitly. */
    if (/country|dial|isd|ccode/.test(hay)) return false;

    return el.type === 'tel' ||
           /phone|mobile|whatsapp/.test(hay) ||
           /contact[_\-\[\] ]*(no|num|number|phone|mobile)/.test(hay);
  }

  function scopeOf(el) {
    return el.closest('form') ||
           el.closest('[id*="HiloSP" i]') ||
           el.closest('[class*="hilo" i]') ||
           document;
  }

  /* Returns the dialling code as digits, or '' when there is no such field.
     1. An explicit [data-hilo-cc] marker (a select/input value, or the text of a
        button label such as the booking form's "+971"). Forms should use this.
     2. Otherwise a select/input whose name, id, class, placeholder or
        aria-label says country / dial / isd. */
  function countryCodeFor(input) {
    var scope = scopeOf(input);
    if (scope === document) {
      /* A field outside any form (the Talk to Stylist booking modal) still has
         its own country-code label right beside it. Use the closest one, at
         most three levels up, so a label elsewhere on the page is never borrowed. */
      var p = input.parentElement;
      for (var up = 0; p && up < 3; up++, p = p.parentElement) {
        var near = p.querySelector('[data-hilo-cc]');
        if (near) {
          var nv = ('value' in near && near.tagName !== 'BUTTON') ? near.value : near.textContent;
          return digits(nv);
        }
      }
      return '';
    }
    var marked = scope.querySelector('[data-hilo-cc]');
    if (marked) {
      var mv = ('value' in marked && marked.tagName !== 'BUTTON') ? marked.value : marked.textContent;
      var md = digits(mv);
      if (md) return md;
    }
    var els = scope.querySelectorAll('select, input');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el === input) continue;
      var hay = ((el.name || '') + ' ' + (el.id || '') + ' ' +
                 (el.className || '') + ' ' +
                 (el.getAttribute('placeholder') || '') + ' ' +
                 (el.getAttribute('aria-label') || '')).toLowerCase();
      if (/country|dial|isd|ccode/.test(hay)) {
        var d = digits(el.value);
        if (d) return d;
      }
    }
    return '';
  }

  /* The national number: drops a typed copy of the selected country code
     ("+91 98765 43210" -> 9876543210, "00971 50…" -> 50…) and the Indian
     trunk prefix 0 ("09876543210" -> 9876543210). */
  function nationalNumber(d, code) {
    var isIndia = (code === '' || code === '91');
    if (code && d.indexOf('00' + code) === 0) d = d.slice(code.length + 2);
    if (isIndia) {
      if (d.length === 12 && d.indexOf('91') === 0) d = d.slice(2);
      else if (d.length === 11 && d.charAt(0) === '0') d = d.slice(1);
    } else {
      if (d.indexOf(code) === 0 && d.length > code.length + INTL_MIN - 1) {
        d = d.slice(code.length);
      }
      /* Most countries also write a trunk 0 at home that is dropped after the
         country code ("+971 050 123 4567" -> 501234567). Italy, San Marino
         and the Vatican keep it as part of the number. */
      if (d.charAt(0) === '0' && d.length > INTL_MIN && !/^(39|378|379)$/.test(code)) {
        d = d.slice(1);
      }
    }
    return d;
  }

  /* Rewrites the field to the national number so the form's own logic sees it. */
  function normalise(input) {
    var d = digits(input.value);
    if (!d) return;
    var n = nationalNumber(d, countryCodeFor(input));
    if (n !== input.value) input.value = n;
  }

  /* Returns an error string, or '' when the number is acceptable. */
  function problemWith(input) {
    var code = countryCodeFor(input);
    var d    = nationalNumber(digits(input.value), code);
    var isIndia = (code === '' || code === '91');

    if (isIndia) {
      if (d.length !== IN_LEN) return MSG_IN_LEN;
      if (!/^[6-9]/.test(d))   return MSG_IN_START;
    } else {
      if (d.length < INTL_MIN || d.length > INTL_MAX) return MSG_INTL;
    }

    /* 0000000000, 9999999999 and similar are placeholders, not numbers. */
    if (/^(\d)\1+$/.test(d)) return MSG_FAKE;
    return '';
  }

  function errorNodeFor(input) {
    var found = input.parentNode && input.parentNode.querySelector('.hilo-phone-err');
    if (found) return found;
    var p = document.createElement('div');
    p.className = 'hilo-phone-err';
    p.setAttribute('role', 'alert');
    p.style.cssText = 'color:#b3261e;font-size:12px;line-height:1.4;margin-top:6px;display:none;';
    if (input.parentNode) input.parentNode.appendChild(p);
    return p;
  }

  function showError(input, msg) {
    var node = errorNodeFor(input);
    node.textContent = msg;
    node.style.display = msg ? 'block' : 'none';
    input.style.borderColor = msg ? '#b3261e' : '';
    if (msg) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  /* ---- as they type: digits only, capped ---- */
  document.addEventListener('input', function (e) {
    var el = e.target;
    if (!isPhoneField(el)) return;

    var clean = digits(el.value).slice(0, HARD_MAX);
    if (el.value !== clean) {
      var atEnd = el.selectionStart === el.value.length;
      el.value = clean;
      /* Only move the caret if it was already at the end, otherwise editing
         the middle of a number becomes impossible. */
      if (atEnd) { try { el.setSelectionRange(clean.length, clean.length); } catch (x) {} }
    }

    /* Clear the error as soon as it becomes valid — nagging mid-typing is the
       fastest way to lose someone. */
    if (!problemWith(el)) showError(el, '');
  }, true);

  /* Tidy the number when the customer leaves the field, so they can see it. */
  document.addEventListener('blur', function (e) {
    var el = e.target;
    if (isPhoneField(el)) normalise(el);
  }, true);

  /* Changing the country code can make a valid number invalid, or the reverse. */
  document.addEventListener('change', function (e) {
    var hay = ((e.target.name || '') + ' ' + (e.target.id || '') + ' ' +
               (e.target.className || '') + ' ' +
               (e.target.hasAttribute && e.target.hasAttribute('data-hilo-cc') ? 'country' : '')).toLowerCase();
    if (!/country|dial|isd|ccode/.test(hay)) return;
    var scope = scopeOf(e.target);
    if (scope === document) return;
    var ins = scope.querySelectorAll('input');
    for (var i = 0; i < ins.length; i++) {
      if (isPhoneField(ins[i]) && ins[i].value) {
        showError(ins[i], '');   /* clear, do not scold yet */
      }
    }
  }, true);

  function prepare(el) {
    if (el.dataset.hiloPhone) return;
    el.dataset.hiloPhone = '1';
    el.setAttribute('inputmode', 'numeric');
    el.setAttribute('autocomplete', 'tel');
    if (!el.getAttribute('maxlength')) el.setAttribute('maxlength', String(HARD_MAX));
  }

  function scan(root) {
    var list = (root || document).querySelectorAll('input');
    for (var i = 0; i < list.length; i++) if (isPhoneField(list[i])) prepare(list[i]);
  }

  scan(document);
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) if (added[j].nodeType === 1) scan(added[j]);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ---- the block ---- */
  function checkAndMaybeBlock(e, container) {
    var inputs = container.querySelectorAll('input');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (!isPhoneField(el)) continue;
      if (el.offsetParent === null) continue;      /* hidden, skip */

      normalise(el);
      var msg = problemWith(el);
      if (msg) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        showError(el, msg);
        try { el.focus(); } catch (x) {}
        return true;
      }
      showError(el, '');
    }
    return false;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest
      ? e.target.closest('button, input[type="submit"], [role="button"]')
      : null;
    if (!btn || btn.type === 'reset') return;

    var container = scopeOf(btn);
    if (container === document) return;

    /* Guard submits only — never a close, cancel or back control. */
    var label = (btn.textContent || btn.value || '').toLowerCase();
    if (/close|cancel|back|×|✕/.test(label)) return;

    checkAndMaybeBlock(e, container);
  }, true);

  document.addEventListener('submit', function (e) {
    checkAndMaybeBlock(e, e.target);
  }, true);
})();